import { useContext, useEffect, useRef, useState } from "react";
import { CommentOutlined, PictureOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Modal, Pagination } from "antd";
import { LangContext, UserDataContext } from "../../contexts/Contexts";
import { StandardEmptyState } from "../uiElements/StandardEmptyState";
import CommentsReactionsBar from "./CommentsReactionsBar";
import CommentItem from "./CommentItem";
import {
  COMMENT_IMAGE_MAX_BYTES,
  COMMENT_IMAGE_MAX_COUNT,
  COMMENT_IMAGE_TYPES,
  ERROR_CANTIDAD,
  ERROR_FORMATO,
  ERROR_TAMANO,
  validarAdjuntosComentario,
} from "./commentsAttachments";
import {
  idsDeAutoresPendientes,
  resolveAutorComentario,
} from "./commentsAuthor";
import { resolveCommentsTexts } from "./commentsTexts";
import { useProfileImage } from "../../../infrastructure/hooks/useProfileImagen";
import { getUserInfoService } from "../../../infrastructure/services/UserInfoService";
import { EVENT_REACTION_TYPES } from "../../../shared/constants/eventReactions";
import { getApiErrorMessage } from "../../../shared/utils/apiErrorMessage";
import {
  EVENT_COMMENT_MAX_LENGTH,
  getTextValidationState,
} from "../../../shared/constants/textLimits";
import { showErrorMessage, showSuccessMessage } from "../utils/messages";
import "./Styles/Comments.css";

// SECCION DE COMENTARIOS REUTILIZABLE.
//
// Es la seccion de comentar que nacio en el detalle del evento (VIVA-353), sin lo
// que la ataba a eventos. La misma pantalla —barra de reacciones, caja de
// escritura con imagenes, pila paginada, editar, eliminar con confirmacion y "me
// gusta" por comentario— sirve para cualquier recurso que se pueda comentar:
// eventos, guias, o lo que venga.
//
// COMO SE VUELVE ESPECIFICA: por la prop `acciones`, que es el UNICO punto donde
// esta seccion toca el backend. No importa ningun servicio de dominio; quien la
// monta le pasa las funciones ya cableadas a SU service, y con eso se respeta el
// flujo del proyecto (Page -> Service -> Repository -> AxiosInstance): la pantalla
// sigue siendo quien decide a que dominio le habla.
//
//   acciones = {
//     listar:      async ({ recursoId, idUsuario })
//                    -> { comentarios: [...] } | [...]
//     publicar:    async ({ recursoId, idUsuario, texto, imagenes })
//                    -> { idComentario } (opcional, pero recomendado)
//     editar:      async ({ recursoId, idUsuario, idComentario, texto })
//     eliminar:    async ({ recursoId, idUsuario, idComentario, esAutor,
//                           esModerador, motivo })
//     reaccionarRecurso:    async ({ recursoId, idUsuario, reaccion })
//                             -> { activa, conteos }
//     reaccionarComentario: async ({ recursoId, idUsuario, idComentario })
//                             -> { activa, total }
//   }
//
// Cada accion es OPCIONAL y define la capacidad: si no viene `editar`, la tarjeta
// no ofrece editar. `capacidades` sirve para apagar una que si existe —el
// interruptor de `eventCommentsBackend.js` traducido a props— sin quitar el
// cableado.
//
// FORMA DE UN COMENTARIO. `listar` devuelve los comentarios YA NORMALIZADOS; el
// mapeo de la respuesta cruda pertenece al mapper/service de cada dominio, no
// aqui:
//
//   { idComentario, idUsuario, autor, foto, fecha, texto,
//     imagenes: [{ id, url, nombre }],
//     totalReacciones, reaccionActiva, editado }
//
// QUE PASA SI FALTA UNA ACCION DE ESCRITURA: la caja de escribir, la barra de
// reacciones y el "me gusta" SIGUEN EN PANTALLA y funcionan contra el estado del
// componente, igual que hacia la maqueta de VIVA-353 mientras no habia endpoints.
// Es a proposito: sirve para revisar el diseño y hacer demos, y el dia que llegue
// el contrato solo hay que pasar la accion. Lo que si se esconde es editar y
// eliminar, porque ahi no hay nada que simular: un menu que dice "Guardar" y no
// guarda no es una maqueta, es una mentira.
//
// PAGINACION: paginas numeradas, con el mismo control y el mismo aspecto que la
// lista principal de eventos (`NewEventList`) — Anterior · 1 2 3 · Siguiente y la
// linea "Pagina X de Y" debajo. El corte se hace EN CLIENTE sobre la lista
// completa, porque los endpoints de este tipo suelen paginar por cursor y no
// decir cuantas paginas hay; `listar` debe devolver el hilo completo.

// Comparacion superficial de los conteos: son un objeto plano de clave de reaccion
// a numero, asi que con esto alcanza. Existe para que el efecto que sincroniza los
// conteos que llegan por prop no entre en bucle (ver abajo).
const mismosConteos = (a = {}, b = {}) => {
  const clavesA = Object.keys(a);
  const clavesB = Object.keys(b);
  if (clavesA.length !== clavesB.length) return false;
  return clavesA.every((clave) => Number(a[clave]) === Number(b[clave]));
};

const extraerComentarios = (respuesta) => {
  if (Array.isArray(respuesta)) return respuesta;
  if (Array.isArray(respuesta?.comentarios)) return respuesta.comentarios;
  return [];
};

export const CommentsSection = ({
  // Id del recurso que se comenta: el evento, la guia, lo que sea. Viaja tal cual
  // a cada accion, asi que el adaptador lo recibe con el nombre que ya conoce.
  recursoId,
  acciones = {},
  // Apaga capacidades que SI tienen accion cableada, por llave:
  // `{ eliminar: false }`. Nunca enciende una que no exista.
  capacidades = {},
  // Habilita moderar: eliminar comentarios ajenos. Lo decide quien monta la
  // seccion (en eventos, `isEventOrganizer`) y se recibe como prop en vez de
  // recalcularlo aqui para no tener dos fuentes de verdad sobre quien modera.
  puedeModerar = false,
  mostrarReacciones = true,
  conteosReacciones = {},
  catalogoReacciones = EVENT_REACTION_TYPES,
  mostrarMeGusta = true,
  adjuntos = {},
  comentariosIniciales = [],
  tamanoPagina = 10,
  maxCaracteres = EVENT_COMMENT_MAX_LENGTH,
  // Reemplaza llaves sueltas del diccionario: `{ emptyText: "..." }` o
  // `{ es: {...}, en: {...} }`. Ver `commentsTexts.js`.
  textos: textosPropios = {},
  className = "",
  icono = <CommentOutlined />,
  // Como se resuelve el nombre y la foto de quien comento. Por omision el mismo
  // servicio de usuarios que usa el feed, que ademas cachea y agrupa peticiones.
  resolverPerfil = getUserInfoService,
}) => {
  const { lang } = useContext(LangContext);
  const { userData } = useContext(UserDataContext);
  const { profileSrc } = useProfileImage(userData);

  const adjuntosHabilitados = adjuntos.habilitados !== false;
  const maxImagenes = adjuntos.maxCantidad ?? COMMENT_IMAGE_MAX_COUNT;
  const maxBytes = adjuntos.maxBytes ?? COMMENT_IMAGE_MAX_BYTES;
  const tiposImagen = adjuntos.tipos ?? COMMENT_IMAGE_TYPES;

  const textos = resolveCommentsTexts({
    lang,
    overrides: textosPropios,
    maxCaracteres,
    maxImagenes,
  });

  // Una capacidad existe cuando hay accion cableada y nadie la apago por prop.
  const puede = (nombre) =>
    capacidades[nombre] !== false && typeof acciones[nombre] === "function";

  const [comentario, setComentario] = useState("");
  const [imagenes, setImagenes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [errorCampo, setErrorCampo] = useState("");
  const [comentariosLocales, setComentariosLocales] = useState([]);
  // Lista COMPLETA que viene del backend; el corte en paginas se hace mas abajo.
  const [comentariosRemotos, setComentariosRemotos] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [cargando, setCargando] = useState(false);
  const [errorListado, setErrorListado] = useState("");
  // Perfiles de los autores, por idUsuario. Los listados no traen ni nombre real
  // ni foto —solo el arroba y nulos—, asi que hay que resolverlos aparte (ver
  // `commentsAuthor.js`).
  const [perfiles, setPerfiles] = useState({});
  // Comentario en espera de confirmacion de borrado, y el motivo cuando se modera.
  const [porEliminar, setPorEliminar] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [eliminando, setEliminando] = useState(false);
  // Reaccion propia y su conteo, en local: una sola reaccion activa por usuario, y
  // volver a presionarla la quita.
  const [reaccionMia, setReaccionMia] = useState(null);
  const [conteos, setConteos] = useState(conteosReacciones);

  // Los conteos guardados suelen llegar con el detalle del recurso, que se pide
  // despues de montar: `useState` solo lee el valor inicial, asi que sin esto la
  // barra se quedaria en cero aunque el backend ya diga cuantas reacciones hay.
  //
  // La comparacion es contra la PROP ANTERIOR, no contra el estado, y las dos cosas
  // que evita son distintas:
  //
  //   * Comparar identidades entra en bucle. El valor por omision es un objeto
  //     literal, asi que es uno nuevo en cada render: el efecto llamaria a setState,
  //     eso provocaria otro render, y de ahi no sale.
  //   * Comparar contra el ESTADO pisa al servidor. Tras reaccionar, el estado trae
  //     los conteos que contesto el backend y la prop sigue siendo la del detalle:
  //     como no coinciden, cada render volveria a imponer la del detalle y el numero
  //     recien llegado duraria un instante.
  const conteosDeLaProp = useRef(conteosReacciones);
  useEffect(() => {
    if (mismosConteos(conteosDeLaProp.current, conteosReacciones)) return;
    conteosDeLaProp.current = conteosReacciones;
    setConteos(conteosReacciones);
  }, [conteosReacciones]);

  const inputArchivo = useRef(null);
  // Turno de la ultima reaccion enviada al recurso. Sirve para descartar respuestas
  // que llegaron tarde: si el usuario cambia de reaccion dos veces seguidas, la que
  // debe quedar pintada es la del ultimo clic, no la que conteste primero.
  const turnoReaccion = useRef(0);
  // La key no puede salir de `Date.now()`: dos comentarios seguidos caerian en el
  // mismo milisegundo y React veria la misma key.
  const contadorLocal = useRef(0);

  // Las vistas previas son object URLs y hay que liberarlas, pero NO cuando
  // cambia `imagenes`: al publicar, la lista se vacia y las URLs pasan a ser del
  // comentario apilado, que las sigue usando. Se guardan aparte y se liberan al
  // desmontar; la que el usuario quita se libera en el momento.
  const urlsCreadas = useRef([]);
  useEffect(
    () => () => {
      urlsCreadas.current.forEach((url) => URL.revokeObjectURL(url));
      urlsCreadas.current = [];
    },
    []
  );

  // Carga completa del hilo. Se guarda en un ref para poder llamarla desde el
  // efecto de montaje y desde el boton de reintentar sin volver a crearla, y
  // porque `acciones` suele llegar como objeto literal —seria una identidad nueva
  // en cada render y el efecto se dispararia en bucle.
  const cargarComentarios = useRef(null);
  cargarComentarios.current = async () => {
    if (!puede("listar")) return;
    if (!recursoId || !userData?.idUsuario) return;

    try {
      setCargando(true);
      setErrorListado("");
      const respuesta = await acciones.listar({
        recursoId,
        idUsuario: userData.idUsuario,
      });
      setComentariosRemotos(extraerComentarios(respuesta));
    } catch (error) {
      console.error("Error al cargar los comentarios", error);
      setErrorListado(getApiErrorMessage(error) || textos.errorListado);
    } finally {
      setCargando(false);
    }
  };

  // Al entrar. Se vuelve a pedir si cambia el recurso o si el usuario termina de
  // cargar despues del primer render.
  useEffect(() => {
    setPaginaActual(1);
    cargarComentarios.current?.();
  }, [recursoId, userData?.idUsuario]);

  // Resolver los perfiles de quienes comentaron. Se dispara cuando cambia la pila.
  //
  // Se piden solo los que faltan y sin repetir: un hilo suele tener varios
  // comentarios de la misma persona, y sin deduplicar seria una peticion por
  // comentario. `getUserInfoService` ademas cachea y agrupa las peticiones en vuelo,
  // asi que dos comentarios del mismo autor comparten una sola llamada.
  //
  // Un perfil que falla se guarda como `null` a proposito: asi queda marcado como
  // "ya se intento" y no se vuelve a pedir en bucle en cada render. Ese comentario
  // se queda con el arroba que mando el backend, que es feo pero identifica.
  useEffect(() => {
    if (typeof resolverPerfil !== "function") return undefined;

    const pendientes = idsDeAutoresPendientes({
      comentarios: comentariosRemotos,
      perfiles,
      idUsuarioActual: userData?.idUsuario,
    });
    if (pendientes.length === 0) return undefined;

    let vigente = true;
    Promise.all(
      pendientes.map(async (id) => {
        try {
          return [id, await resolverPerfil(id)];
        } catch (error) {
          console.error("No se pudo resolver el autor del comentario", id, error);
          return [id, null];
        }
      })
    ).then((resueltos) => {
      if (!vigente) return;
      setPerfiles((previos) => ({
        ...previos,
        ...Object.fromEntries(resueltos),
      }));
    });

    return () => {
      vigente = false;
    };
  }, [comentariosRemotos, perfiles, userData?.idUsuario, resolverPerfil]);

  const validacion = getTextValidationState(comentario, { max: maxCaracteres });
  const hayContenido = !validacion.isEmpty || imagenes.length > 0;
  const puedeEnviar = hayContenido && !validacion.isTooLong && !enviando;

  // Los recien publicados van primero: del mas reciente al mas antiguo, que es
  // como devuelven el listado los endpoints. Se filtra por id para que un
  // comentario que se acaba de publicar no aparezca dos veces cuando el listado lo
  // devuelva; los locales (los apilados sin backend) no tienen equivalente remoto.
  const yaApilados = new Set(comentariosLocales.map((item) => item.idComentario));
  const listaComentarios = [
    ...comentariosLocales,
    ...comentariosRemotos.filter((item) => !yaApilados.has(item.idComentario)),
    ...comentariosIniciales,
  ];
  const total = listaComentarios.length;

  const totalPaginas = Math.max(1, Math.ceil(total / tamanoPagina));
  // Si la lista encoge —se borro un comentario, o cambio el recurso— la pagina en
  // la que estaba el usuario puede quedar fuera de rango y la pantalla saldria
  // vacia sin explicacion. Se acota al pintar en vez de corregir el estado, para
  // no encadenar renders.
  const paginaVisible = Math.min(paginaActual, totalPaginas);
  const comentariosPagina = listaComentarios.slice(
    (paginaVisible - 1) * tamanoPagina,
    paginaVisible * tamanoPagina
  );

  // Mismo control y mismas etiquetas que la lista principal de eventos
  // (`NewEventList`): antd pinta "<" y ">" por omision y ahi se sustituyen por
  // Anterior/Siguiente.
  const renderPaginationItem = (page, type, originalElement) => {
    if (type === "prev") {
      return <button className="vn-comments__pagination-nav">{textos.anterior}</button>;
    }
    if (type === "next") {
      return <button className="vn-comments__pagination-nav">{textos.siguiente}</button>;
    }
    return originalElement;
  };

  // Un comentario es propio si lo escribio quien esta viendo la pantalla. Los recien
  // publicados guardan `idUsuario` de la sesion y los del listado lo traen del
  // backend, asi que la comparacion sirve para los dos.
  const esComentarioPropio = (item) =>
    Boolean(userData?.idUsuario) && item?.idUsuario === userData.idUsuario;

  const nombreAutor =
    [userData?.nombres, userData?.apellidos].filter(Boolean).join(" ") ||
    userData?.nombreCompleto ||
    userData?.nombre ||
    textos.autorFallback;

  const handleChange = (event) => {
    if (errorCampo) setErrorCampo("");
    setComentario(event.target.value);
  };

  const handleSeleccionImagenes = (event) => {
    const elegidas = Array.from(event.target.files || []);
    // El input se limpia siempre, para poder volver a elegir el mismo archivo.
    event.target.value = "";
    if (elegidas.length === 0) return;

    const { aceptadas, error } = validarAdjuntosComentario(
      elegidas,
      imagenes.length,
      { maxCantidad: maxImagenes, maxBytes, tipos: tiposImagen }
    );

    if (error === ERROR_FORMATO) setErrorCampo(textos.errorFormato);
    else if (error === ERROR_TAMANO) setErrorCampo(textos.errorTamano);
    else if (error === ERROR_CANTIDAD) setErrorCampo(textos.errorCantidad);
    else setErrorCampo("");

    if (aceptadas.length === 0) return;

    const nuevas = aceptadas.map((archivo) => ({
      id: `img-${(contadorLocal.current += 1)}`,
      nombre: archivo.name,
      url: URL.createObjectURL(archivo),
      // El `File` se conserva porque es lo que viaja en el multipart; la miniatura
      // solo necesita la URL.
      archivo,
    }));
    urlsCreadas.current.push(...nuevas.map((imagen) => imagen.url));

    setImagenes((previas) => [...previas, ...nuevas]);
  };

  const quitarImagen = (id) => {
    setImagenes((previas) => {
      const quitada = previas.find((imagen) => imagen.id === id);
      if (quitada) {
        URL.revokeObjectURL(quitada.url);
        urlsCreadas.current = urlsCreadas.current.filter(
          (url) => url !== quitada.url
        );
      }
      return previas.filter((imagen) => imagen.id !== id);
    });
  };

  // `creado` es lo que devolvio la accion `publicar`. Cuando trae `idComentario`,
  // el comentario se apila con su id REAL en vez de una key inventada: asi coincide
  // con el que devolvera el listado y se puede deduplicar, y es el id que
  // necesitarian editar y eliminar.
  const apilarComentario = (creado) => {
    setComentariosLocales((previos) => [
      {
        idComentario:
          creado?.idComentario ?? `local-${(contadorLocal.current += 1)}`,
        idUsuario: userData?.idUsuario,
        autor: nombreAutor,
        foto: profileSrc,
        fecha: new Date().toISOString(),
        texto: comentario.trim(),
        imagenes,
      },
      ...previos,
    ]);
    setComentario("");
    // Las imagenes pasan a ser del comentario: aqui NO se revocan sus URLs, la
    // miniatura publicada las sigue usando (se liberan al desmontar).
    setImagenes([]);
    // El comentario nuevo entra al principio de la pila, asi que si el usuario
    // estaba en la pagina 3 no lo veria. Se le regresa a la primera.
    setPaginaActual(1);
    showSuccessMessage(textos.exito);
  };

  const publicarComentario = async () => {
    if (!hayContenido) {
      setErrorCampo(textos.errorVacio);
      return;
    }
    if (validacion.isTooLong) {
      setErrorCampo(textos.errorLargo);
      return;
    }

    setErrorCampo("");

    // Sin accion de publicar la caja sigue sirviendo, pero solo en memoria: nada
    // sale a la red y lo escrito se pierde al recargar.
    if (!puede("publicar")) {
      apilarComentario();
      return;
    }

    try {
      setEnviando(true);
      const creado = await acciones.publicar({
        recursoId,
        idUsuario: userData?.idUsuario,
        texto: comentario,
        imagenes: adjuntosHabilitados
          ? imagenes.map((imagen) => imagen.archivo).filter(Boolean)
          : [],
      });
      // Se apila con la respuesta en vez de volver a pedir el listado: recargar
      // descartaria lo que el usuario ya trajo y lo mandaria de vuelta al principio.
      apilarComentario(creado);
    } catch (error) {
      console.error("Error al publicar el comentario", error);
      // El motivo real del rechazo viene en el cuerpo (ASP.NET responde
      // ValidationProblemDetails, sin `message`). Se cuelga tambien del campo
      // porque el aviso de antd dura 3 segundos y se desvanece.
      const razon = getApiErrorMessage(error) || textos.errorEnvio;
      setErrorCampo(razon);
      showErrorMessage(razon);
    } finally {
      setEnviando(false);
    }
  };

  // Reemplaza un comentario en las dos pilas: la local (lo recien publicado) y la
  // remota (lo que trajo el listado). Un comentario vive en una o en la otra segun
  // cuando aparecio, y quien edita no tiene por que saber en cual.
  const reemplazar = (idComentario, cambios) => {
    const aplicar = (lista) =>
      lista.map((item) =>
        item.idComentario === idComentario ? { ...item, ...cambios } : item
      );
    setComentariosLocales(aplicar);
    setComentariosRemotos(aplicar);
  };

  const quitar = (idComentario) => {
    const filtrar = (lista) =>
      lista.filter((item) => item.idComentario !== idComentario);
    setComentariosLocales(filtrar);
    setComentariosRemotos(filtrar);
  };

  // El texto se cambia en pantalla DESPUES de que el backend confirma, no antes: un
  // comentario es contenido publicado, y mostrarlo ya editado mientras la peticion
  // viaja haria creer que se guardo aunque falle.
  const editarComentario = async (idComentario, textoNuevo) => {
    try {
      await acciones.editar({
        recursoId,
        idUsuario: userData?.idUsuario,
        idComentario,
        texto: textoNuevo,
      });
      reemplazar(idComentario, { texto: textoNuevo, editado: true });
      showSuccessMessage(textos.editado);
    } catch (error) {
      console.error("Error al actualizar el comentario", error);
      showErrorMessage(getApiErrorMessage(error) || textos.errorEditar);
      // Se relanza para que la tarjeta deje la caja de edicion abierta con el texto
      // que el usuario escribio, en vez de tragarselo.
      throw error;
    }
  };

  const pedirEliminar = (idComentario) => {
    const item = listaComentarios.find((c) => c.idComentario === idComentario);
    if (!item) return;
    setMotivo("");
    setPorEliminar(item);
  };

  const confirmarEliminar = async () => {
    if (!porEliminar) return;
    const esAutor = esComentarioPropio(porEliminar);
    try {
      setEliminando(true);
      await acciones.eliminar({
        recursoId,
        idUsuario: userData?.idUsuario,
        idComentario: porEliminar.idComentario,
        esAutor,
        // Moderar es actuar sobre contenido AJENO: quien modera borrando algo suyo
        // lo esta borrando como autor, no moderando. La traduccion al campo que
        // espere cada backend es cosa del adaptador.
        esModerador: puedeModerar && !esAutor,
        motivo: motivo.trim(),
      });
      quitar(porEliminar.idComentario);
      showSuccessMessage(esAutor ? textos.eliminado : textos.removido);
      setPorEliminar(null);
      setMotivo("");
    } catch (error) {
      console.error("Error al eliminar el comentario", error);
      showErrorMessage(getApiErrorMessage(error) || textos.errorEliminar);
    } finally {
      setEliminando(false);
    }
  };

  // Reaccionar al recurso.
  //
  // La regla de "una sola reaccion activa" la resuelve el backend: mandar la misma
  // otra vez la quita y mandar otra reemplaza a la anterior. Por eso aqui NO se
  // decide entre insertar y borrar; se manda la que el usuario eligio y se pinta lo
  // que conteste, que ademas trae los conteos de todo el mundo ya recalculados.
  //
  // Se mueve la pantalla antes de que llegue la respuesta porque una reaccion es un
  // gesto y esperar se siente trabado; si falla, se regresa al estado anterior.
  const seleccionarReaccion = async (id) => {
    const esLaMisma = reaccionMia === id;
    const reaccionPrevia = reaccionMia;
    const conteosPrevios = conteos;

    setConteos((previos) => {
      const siguientes = { ...previos };
      if (reaccionPrevia) {
        siguientes[reaccionPrevia] = Math.max(
          0,
          Number(siguientes[reaccionPrevia] ?? 0) - 1
        );
      }
      if (!esLaMisma) {
        siguientes[id] = Number(siguientes[id] ?? 0) + 1;
      }
      return siguientes;
    });
    setReaccionMia(esLaMisma ? null : id);

    if (!puede("reaccionarRecurso")) return;
    if (!recursoId || !userData?.idUsuario) return;

    const miTurno = (turnoReaccion.current += 1);

    try {
      const resultado = await acciones.reaccionarRecurso({
        recursoId,
        idUsuario: userData.idUsuario,
        reaccion: id,
      });
      if (miTurno !== turnoReaccion.current) return;
      // El servidor manda: sus conteos incluyen a los demas usuarios y su `activa`
      // ya refleja si la reaccion quedo puesta o quitada.
      if (resultado) {
        setReaccionMia(resultado.activa ?? null);
        setConteos(resultado.conteos ?? {});
      }
    } catch (error) {
      console.error("Error al reaccionar", error);
      if (miTurno !== turnoReaccion.current) return;
      setReaccionMia(reaccionPrevia);
      setConteos(conteosPrevios);
      showErrorMessage(getApiErrorMessage(error) || textos.errorReaccion);
    }
  };

  // "Me gusta" de un comentario. La tarjeta pinta el cambio y se queda con lo que
  // devuelva esta funcion; si lanza, ella sola revierte. El aviso se da aqui porque
  // es la seccion la que habla con el backend.
  //
  // EL AGREGADO SE GUARDA EN LA LISTA, no solo en la tarjeta. La pila se pagina en
  // cliente, asi que pasar a la pagina siguiente DESMONTA las tarjetas de la
  // primera; si el agregado viviera unicamente en el estado de la tarjeta, al
  // volver se remontaria con el valor viejo del listado y el me gusta apareceria
  // apagado aunque el servidor lo tenga guardado.
  const reaccionarComentario = async (idComentario) => {
    if (!puede("reaccionarComentario")) return null;
    if (!userData?.idUsuario) return null;

    try {
      const resultado = await acciones.reaccionarComentario({
        recursoId,
        idUsuario: userData.idUsuario,
        idComentario,
      });
      // Mismas llaves que trae el listado, para que la lista tenga una sola forma
      // venga de donde venga.
      reemplazar(idComentario, {
        totalReacciones: Number(resultado?.total) || 0,
        reaccionActiva: Boolean(resultado?.activa),
      });
      return resultado;
    } catch (error) {
      console.error("Error al reaccionar al comentario", error);
      showErrorMessage(getApiErrorMessage(error) || textos.errorReaccion);
      throw error;
    }
  };

  return (
    <section className={`vn-comments ${className}`.trim()}>
      <div className="vn-comments__heading">
        <span className="vn-comments__heading-icon">{icono}</span>
        <h2 className="vn-comments__title">{textos.title}</h2>
        {total > 0 && <span className="vn-comments__count">{total}</span>}
      </div>

      {mostrarReacciones && (
        <CommentsReactionsBar
          catalogo={catalogoReacciones}
          counts={conteos}
          selected={reaccionMia}
          onSelect={seleccionarReaccion}
          etiquetaGrupo={textos.reacciones}
        />
      )}

      <div className="vn-comment-composer">
        <textarea
          className={`vn-comment-input ${
            errorCampo ? "vn-comment-input--error" : ""
          }`}
          placeholder={textos.placeholder}
          maxLength={maxCaracteres}
          rows={3}
          value={comentario}
          onChange={handleChange}
          disabled={enviando}
          aria-label={textos.placeholder}
          aria-invalid={Boolean(errorCampo)}
        />

        {imagenes.length > 0 && (
          <ul className="vn-comment-previews">
            {imagenes.map((imagen) => (
              <li key={imagen.id} className="vn-comment-preview">
                <img src={imagen.url} alt={imagen.nombre} />
                <button
                  type="button"
                  className="vn-comment-preview__remove"
                  aria-label={`${textos.quitarImagen}: ${imagen.nombre}`}
                  onClick={() => quitarImagen(imagen.id)}
                >
                  <CloseOutlined />
                </button>
              </li>
            ))}
          </ul>
        )}

        {errorCampo && (
          <span className="vn-comment-error" role="alert">
            {errorCampo}
          </span>
        )}

        <div className="vn-comment-composer__actions">
          {adjuntosHabilitados && (
            <>
              <input
                ref={inputArchivo}
                type="file"
                accept={tiposImagen.join(",")}
                multiple
                hidden
                data-testid="vn-comment-file-input"
                onChange={handleSeleccionImagenes}
              />
              <button
                type="button"
                className="vn-comment-attach"
                aria-label={textos.attach}
                title={textos.attach}
                onClick={() => inputArchivo.current?.click()}
              >
                <PictureOutlined />
              </button>
            </>
          )}
          <div className="vn-comment-composer__submit-group">
            <span className="vn-comment-counter">
              {validacion.rawLength}/{maxCaracteres}
            </span>
            <Button
              type="primary"
              className="vn-comment-submit"
              onClick={publicarComentario}
              loading={enviando}
              disabled={!puedeEnviar}
            >
              {enviando ? textos.enviando : textos.submit}
            </Button>
          </div>
        </div>
      </div>

      {total > 0 ? (
        <div className="vn-comments__list">
          {comentariosPagina.map((item) => {
            const autorResuelto = resolveAutorComentario({
              comentario: item,
              perfil: perfiles[item.idUsuario],
              usuarioActual: userData,
              fotoUsuarioActual: profileSrc,
            });
            return (
              <CommentItem
                key={item.idComentario}
                idComentario={item.idComentario}
                autor={autorResuelto.nombre}
                foto={autorResuelto.foto}
                fecha={item.fecha}
                texto={item.texto}
                imagenes={item.imagenes}
                totalReacciones={item.totalReacciones}
                reaccionActiva={item.reaccionActiva}
                editado={item.editado}
                maxCaracteres={maxCaracteres}
                textos={textos}
                /* Editar es solo del autor: nadie reescribe lo que dijo otro, ni
                   siquiera quien modera. Eliminar lo puede el autor y tambien
                   quien modera, sobre comentarios ajenos. */
                puedeEditar={puede("editar") && esComentarioPropio(item)}
                puedeEliminar={
                  puede("eliminar") &&
                  (esComentarioPropio(item) || puedeModerar)
                }
                puedeReaccionar={mostrarMeGusta}
                onEditar={editarComentario}
                onEliminar={pedirEliminar}
                onReaccionar={reaccionarComentario}
              />
            );
          })}
        </div>
      ) : (
        /* El vacio solo se afirma cuando ya se pregunto: mientras carga la
           primera tanda decir "se el primero en comentar" seria mentira. */
        !cargando && (
          <StandardEmptyState
            title={textos.emptyTitle}
            description={textos.emptyText}
          />
        )
      )}

      {cargando && (
        <p className="vn-comments__status" role="status">
          {textos.cargando}
        </p>
      )}

      {errorListado && !cargando && (
        <div className="vn-comments__status" role="alert">
          <span className="vn-comment-error">{errorListado}</span>
          <Button type="link" onClick={() => cargarComentarios.current?.()}>
            {textos.reintentar}
          </Button>
        </div>
      )}

      {totalPaginas > 1 && !cargando && (
        <div className="vn-comments__pagination-wrap">
          <div className="vn-comments__pagination">
            <Pagination
              current={paginaVisible}
              total={total}
              pageSize={tamanoPagina}
              onChange={setPaginaActual}
              showSizeChanger={false}
              responsive
              itemRender={renderPaginationItem}
            />
          </div>
          <span className="vn-comments__pagination-info">
            {textos.pagina} {paginaVisible} {textos.de} {totalPaginas}
          </span>
        </div>
      )}

      {/* Confirmacion de borrado. Es destructivo y no se puede deshacer, asi que
          nunca se ejecuta con un solo clic.
          El aviso cambia segun quien borra: al autor se le dice que no hay vuelta
          atras; a quien modera, que va a remover contenido AJENO y que se le
          avisara a su autor, porque no es lo mismo y conviene que lo sepa antes. El
          motivo solo aparece en ese segundo caso: acompaña a la moderacion, no al
          borrado propio. */}
      <Modal
        open={Boolean(porEliminar)}
        title={textos.confirmarTitulo}
        onCancel={() => setPorEliminar(null)}
        onOk={confirmarEliminar}
        okText={textos.confirmar}
        cancelText={textos.cancelar}
        okButtonProps={{ danger: true, loading: eliminando }}
        cancelButtonProps={{ disabled: eliminando }}
        centered
      >
        <p>
          {porEliminar && esComentarioPropio(porEliminar)
            ? textos.confirmarPropio
            : textos.confirmarModeracion}
        </p>

        {porEliminar && !esComentarioPropio(porEliminar) && (
          <label className="vn-comment-delete-reason">
            <span>{textos.motivoEtiqueta}</span>
            <textarea
              className="vn-comment-input"
              placeholder={textos.motivoPlaceholder}
              aria-label={textos.motivoEtiqueta}
              maxLength={maxCaracteres}
              rows={2}
              value={motivo}
              onChange={(event) => setMotivo(event.target.value)}
              disabled={eliminando}
            />
          </label>
        )}
      </Modal>
    </section>
  );
};

export default CommentsSection;
